/** @module csi/store/Values */
define([
	"dojo/_base/declare",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/underscore",
	"csi/comm",
	"csi/heading/_base",
	"csi/locale",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"./_RequestQueryMixin",
	"./Request"
], function(declare, csi, Deferred, _, comm, csiHeading, locale, xml, Query, QueryProcessor, _RequestQueryMixin, RequestStore) {

	var AUTO_COMPLETE_MIN_CHARS = 3;

	var _emptyItem = {
		_count: 0,
		_refs: null,
		_textCount: "",
		_text: "",
		_value: "__EMPTY__"
	};

	function _addEmptyItem(/*Object[]*/data, /*Boolean*/bCopy) {
		// summary: add empty item if necessary
		if (!data || !data.length) {
			data = [_emptyItem];
		} else if (data[0]._value === "") {
			// if there is an empty entry, then replace value with __EMPTY__
			data[0]._value = _emptyItem._value;
		} else if (data[0]._value != _emptyItem._value) {
			// insert empty entry
			if (bCopy) {
				data = [_emptyItem].concat(data);
			} else {
				data.unshift(_emptyItem);
			}
		}

		return data;
	}

	function _extractValuesFromNodes(/*Context*/ctx, /*Spec*/spec, /*Node[]*/nodes) {
		if (!ctx || !spec || !nodes) {
			return [];
		}

		var cache = ctx.cache;
		var countObjMap = {};
		var h = spec.heading;
		var matrixPath = spec.rootSpec?.matrixGroupStandardPath || spec.customMatrixStandardPath;
		var valueNodes = nodes;

		function hashValue(text, value) {
			var countObj = countObjMap[value];
			if (countObj) {
				countObj._count++;
			} else {
				// Keep the real value as a part of the object. We cannot reuse the key in 'countObjMap' as a value because it gets converted to a string
				countObj = countObjMap[value] = {_count: 1, _text: text, _value: value};
			}

			return countObj;
		}

		// apply extra filter from spec, if any
		if (spec.extraFilter) {
			valueNodes = QueryProcessor.applyQueryToNodes(spec.extraFilter, nodes, ctx.cache);
		}

		if (matrixPath) {
			valueNodes = [];

			_.each(nodes, function(node) {
				var n = cache.getPathNodes(node, matrixPath);
				if (!n || n.length == 0) {
					// hash blank values now since they will get "forgotten" when valueNodes is flattened
					hashValue("", "");
				} else {
					valueNodes.push(n);
				}
			});

			valueNodes = _.flatten(valueNodes);
		}

		ctx.forEach(valueNodes, function(ctx) {
			var obj = csiHeading.toFilterTextAndValue(ctx, h);
			var text = obj.text;
			var value = csiHeading.getClientFilterValue(ctx, spec, obj.value);

			if (_.isArray(value)) {
				_.each(value, function(v, i) {
					hashValue(text[i], v);
				});
			} else {
				hashValue(text, value);
			}
		});

		return _.values(countObjMap);
	}

	/**
	 * Return possible values for boolean attributes
	 * @param {boolean} bBlanks - set to true to add options for blanks and nonblanks
	 * @return {Object[]}
	 */
	function _getBooleanData() {
		return [
			{_text: locale.resource("BooleanFalse"), _value: false, _count: 0},
			{_text: locale.resource("BooleanTrue"), _value: true, _count: 0}
		];
	}


	var _Values = declare([RequestStore, _RequestQueryMixin], {
		queryClass: Query,
		requestBinaryParameter: comm.QUERY_XML,
		requestModule: comm.MOD_SEARCH,
		requestOperation: "ValuesByXML",

		ALL_PROPERTIES: [
			"countProperty",
			"cssProperty",
			"exclusiveProperty",
			"idProperty",
			"indexProperty",
			"labelProperty",
			"labelWithCountProperty",
			"mergedValuesProperty",
			"newProperty",
			"refsProperty"
		],

		// item properties
		countProperty: "_count",
		cssProperty: "_css",
		exclusiveProperty: "_exclusive",
		idProperty: "_value",
		indexProperty: "_index",
		labelProperty: "_text",
		labelWithCountProperty: "_textCount",
		mergedValuesProperty: "_merged",
		newProperty: "_new",
		newCss: "italic",
		refsProperty: "_refs",

		// Boolean: indicate if should auto-complete typed text using server query rather than request all possible values and filter on client
		//bAutoComplete: false,

		// Boolean: indicate if should add items indicating number of blank/non-blank values for filtering
		//bBlankFilters: false,

		// Boolean: indicate if should combine blank items into single item
		//bBlanks: false,

		// Boolean: indicate if should display counts combined with value
		//bCounts: false,

		// Boolean: indicate if should force empty item as first item in store. If left as undefined, then empty value will be treated as any other value.
		// If set explicitly to false, then all empty items will be removed from the store
		//bEmptyItem: undefined,

		/** @property {boolean} bHasRefs - indicates if store items contains refs (urls) for items per value */
		//bHasRefs: false,

		// Boolean: indicate if should ignore current query values for given spec - used when selecting values for query rather than executing query (column filters)
		//bIgnoreQueryBySpec: false,

		/** @property {boolean} bPrefill: if true, then attempt to prefill values when known */
		//bPrefill: false,

		// Context?: view context for store - optional, but necessary when custom operation
		//ctx: null,

		// String[]: parameters in query options that do not apply to querying for values (such as those for paging)
		requestOmitOptions: ["count", "direction", "noActionData", "queryOptions", "start", "store"],

		// String[]: client will filter by _text, not server
		requestOmitQuery: ["_count", "_css", "_index", "_new", "_textCount", "_text", "_value"],

		// String[]: parameters to omit from Values request
		requestOmitParameters: [
			"Crew.Scope",
			"Dep.Child.Attr", "Dep.Child.Level", "Dep.Parent.Attr", "Dep.Parent.Level", "Dep.Path",
			"Fmt.AC.Policies", "Fmt.AC.Rights", "Fmt.Attr.Info", "Fmt.Attr.List", "Fmt.Complete", "Fmt.Complete.Max", "Fmt.Crew", "Fmt.Node.Info"
		],

		// Store?: data store from which values are being extracted
		//sourceStore: null,

		// Spec?: specification for property/attribute for which values exist - if not specified then will treat as generic attribute
		//spec: null,

		// Object[]: selected values for store (used to maintain user selections even when no longer part of query results)
		//_selectedItems: null,

		// Object[]: additional special values to prepend to queried values
		//_specialItems: null,


		addSelectedValue: function(/*String*/value, /*String?*/text, /*Boolean?*/bNew) {
			// summary: provide function to create new item
			var item = this.getFromMemory(value);
			if (!item) {
				if (text == null) {
					text = csiHeading.getTextForFilterValue(this.ctx, this.spec, value);
				}

				item = {
					_count: 0,
					_refs: null,
					_textCount: text,
					_text: text,
					_value: value
				};

				if (bNew) {
					item[this.cssProperty] = this.newCss;
					item[this.newProperty] = true;
				}

				this.put(item);
			}

			this._selectedItems = this._selectedItems || [];
			this._selectedItems.push(item);
		},

		addSpecialValue: function(/*String*/value, /*String?*/text) {
			// summary: add special value that should always appear in store - should be added when store first initialized
			if (text == null) {
				text = csiHeading.getTextForFilterValue(this.ctx, this.spec, value);
			}

			var item = {
				_count: 0,
				_refs: null,
				_textCount: text,
				_text: text,
				_value: value
			};

			this._specialItems = this._specialItems || [];
			this._specialItems.push(item);
		},

		/**
		 * Return item corresponding to given value
		 * @param {any} value
		 * @return {Object}
		 */
		getByValue: function(value) {
			return this.get((value == null) ? xml.VALUE_BLANK : value);
		},

		getEmptyValue: function() {
			// summary: return empty string
			return this.bEmptyItem ? _emptyItem._value : null;
		},

		getRequest: function(/*Object?*/query, /*Object?*/options, /*Boolean?*/bReport, /*Boolean?*/bForce) {
			// summary: add parameters based on spec
			var ctx = this.ctx;
			var spec = this.spec;

			// do not issue request until some query text has been entered
			if (this.bAutoComplete) {
				var queryText = query && String(query._text);
				queryText = queryText ? xml.queryFromMatchValue(queryText) : "";

				if (queryText.length < AUTO_COMPLETE_MIN_CHARS) {
					return null;
				}
			}

			var request = this.inherited(arguments);
			if (request && spec) {
				var source = this.sourceStore;
				var nodes = ctx && source && source.getAllResults && source.getAllResults(this._getServerQuery(query), options);

				// if a query is required and does not exist, then empty result set
				if (!nodes && this.bQueryRequired && (query[spec.queryKey] == null)) {
					nodes = [];
				}

				if (nodes) {
					// if source store exists and has loaded all results, then extract values from known results
					request = new Deferred();
					request.resolve(_extractValuesFromNodes(ctx, spec, nodes));
				} else {
					// continue specifying server request
					var qs = spec.querySpec;

					request.addParameter("Qry.Value.Attr", qs.id);
					request.addParameter("Qry.Value.Index", qs.index);
					request.addParameter("Qry.Value.Path", qs.path);

					if (qs.prop) {
						request.addParameter("Qry.Value.Node", xml.propertyToFilterParameter(qs.prop));
					}

					if (qs.blanksOnly) {
						request.addParameter("Qry.Value.EmptyOnly", "true");
					}

					if (spec.extraFilter) {
						request.binaryQuery.importQuery(spec.extraFilter);
						request.setBinaryQuery(this.requestBinaryParameter, request.binaryQuery);
					}
				}
			}

			return request;
		},

		getResultTotal: function(/*Number?*/nDisplayed) {
			// summary: iterate through results, adding counts along the way
			var data = this._store.data;
			var n = nDisplayed || data.length;
			var count = 0;

			for (var i = 0; i < n; ++i) {
				count += data[i]._count;
			}

			return count;
		},

		/** get the selected items */
		getSelectedItems() {
			return this._selectedItems;
		},

		put: function(/*Object*/object, /*Object?*/directives) {
			// summary: if item being added is new, then add appropriate CSS setting
			if (object[this.newProperty] && !object[this.cssProperty]) {
				object[this.cssProperty] = this.newCss;
			}

			return this.inherited(arguments);
		},

		query: function(/*Object?*/query, /*Object?*/options) {
			// summary: remove spec value from query
			var spec = this.spec;

			if (query && spec && this.bIgnoreQueryBySpec) {
				query = _.omit(query, spec.queryKey);
			}

			return this.inherited(arguments, [query, options]);
		},

		remove: function(/*String*/id) {
			// summary: remove id from store and _specialItems
			if (this._specialItems) {
				this._specialItems = _.reject(this._specialItems, function(item) {
					return item._value == id;
				});
			}

			if (this._selectedItems) {
				this._selectedItems = _.reject(this._selectedItems, function(item) {
					return item._value == id;
				});
			}

			this.inherited(arguments);
		},

		/**
		 * Remove reference to result with given id
		 * @param {string} resultId
		 * @return {Object} - return item if reference to result is found and removed
		 */
		removeRef: function(resultId) {
			if (!this.isLoaded()) {
				return null;
			}

			const {countProperty, refsProperty} = this;
			const data = this._store.data;

			return data.find(item => {
				const refs = item[refsProperty];
				const n = _.indexOf(refs, resultId);

				if (n >= 0) {
					refs.splice(n, 1);
					--item[countProperty];
					return true;
				}
			});
		},

		removeSelectedValue: function(/*String*/id) {
			// summary: remove item with given id from selected list, and also from store if its a newly added value
			var item = this.get(id);
			if (item && item[this.newProperty]) {
				this.remove(id);
			} else {
				this._selectedItems = _.reject(this._selectedItems, function(item) {return item._value == id;});
			}
		},

		removeAllSelectedValues: function() {
			// summary: reset selected values and remove from store unless they were originally there
			_.each(this._selectedItems, function(item) {
				if (item[this.newProperty]) {
					this.remove(item._value);
				}
			}, this);

			this._selectedItems = null;
		},

		/**
		 * Convert given values or nodes to store items
		 * @param {Object[]} resultValues
		 * @param {Node[]} resultNodes
		 */
		_convertToData: function(resultValues, resultNodes) {
			const ctx = this.ctx;
			const spec = this.spec;
			var bRefs = false;
			var values;

			if (resultValues) {
				bRefs = !!(resultValues[0] && resultValues[0].Refs);

				values = _.map(resultValues, function(obj) {
					const v = obj.Type ? obj.Value : null;

					return {
						_refs: obj.Refs,
						_count: Number(obj.Count),
						_text: csiHeading.getTextForFilterValue(ctx, spec, v),
						_value: csiHeading.getClientFilterValue(ctx, spec, v)
					};
				});
			} else {
				// if custom operation, response could contain nodes, so extract values from them
				values = _extractValuesFromNodes(ctx, spec, resultNodes);
			}

			this.bHasRefs = bRefs;
			this._onData(values);
		},

		_getServerQuery: function(/*Object?*/query) {
			// summary: return query object that will be applied on server
			var q = this.inherited(arguments);
			var queryText = query && String(query._text);

			if (this.bAutoComplete && queryText && xml.queryFromMatchValue(queryText)) {
				q[this.spec.queryKey] = queryText;
			}

			return q;
		},

		_indexData: function(/*Object[]*/data) {
			// summary: apply index to each data item
			_.each(data, function(val, i) {
				val[this] = i;
			}, this.indexProperty);
		},

		_onData: function(/*Object[]*/data) {
			// summary: populate store with data
			const {_selectedItems, _specialItems} = this;
			var store = this._store;
			var spec = this.spec;
			var qs = spec && spec.querySpec;
			var type = qs && qs.type;
			var bForceBlanks;
			var bForceNonBlanks;

			// remove null value if not showing blanks, but showing empty item - empty item will take its place
			if (data.length && !this.bBlanks) {
				if (this.bEmptyItem) {
					data.dele(function(item) {return item._value == null;});
				} else if (this.bEmptyItem === false) {
					data.dele(function(item) {return !item._text;});
				}
			}

			// add selected items before sorting
			_.each(_selectedItems, function(item, i) {
				const newValue = {_value: item._value};

				if (!_.findWhere(data, newValue)) {
					if (item._value == xml.VALUE_BLANK) {
						bForceBlanks = true;
					} else if (item._value == xml.VALUE_NONBLANK) {
						bForceNonBlanks = true;
					} else if (!_specialItems || !_.findWhere(_specialItems, newValue)) {
						item._count = 0;
						data.push(item);
					}
				}
			});

			// sort results
			data = this._sortData(data);

			// find index where special values should be inserted which is after any empty value
			var insertIndex = 0;

			if (data[0] && (data[0]._value === "")) {
				insertIndex = 1;
			}

			// add (non) blank items if so desired
			if (this.bBlanks || this.bBlankFilters || bForceBlanks) {
				// count blanks and non-blanks
				var nBlanks = 0;
				var nNonBlanks = 0;
				var blankRefs = this.bHasRefs ? [] : null;
				var nonblankRefs = (this.bHasRefs && this.bBlankFilters) ? [] : null;
				var blanks = [];

				_.each(data, function(obj, i) {
					if (obj._text) {
						nNonBlanks += obj._count;

						if (nonblankRefs) {
							nonblankRefs.push(obj._refs);
						}
					} else {
						nBlanks += obj._count;
						blanks.push(i);

						if (blankRefs) {
							blankRefs.push(obj._refs);
						}
					}
				}, this);

				if (nBlanks) {
					// remove blank items from list - do so in reverse order so indices will match
					bForceBlanks = true;
					insertIndex = 0;
					blanks.reverse();

					_.each(blanks, function(i) {
						data.splice(i, 1);
					});
				}

				if (this.bBlankFilters) {
					// if query was for only blank and non-blank values, then remove all entries - they will be added back below
					if (qs && qs.blanksOnly) {
						bForceBlanks = true;
						bForceNonBlanks = true;
						data = [];
					} else if (nBlanks) {
						// if there are blanks, then also add non-blanks - only do this when blanks are forced as non-blanks is a
						// special value that is just a group of other values
						bForceNonBlanks = !!nNonBlanks;
					}

					// some queries decision was to not show counts when only showing blanks/non-blanks options, and some queries do not
					// support correct blank counts
					if (qs && qs.omitBlankCounts) {
						bForceBlanks = true;
						nBlanks = 0;
						nNonBlanks = 0;
					}
				}

				// if there are values, but no counts, then force display (prefill use case)
				if (data.length && (this._prefilled || (!nBlanks && !nNonBlanks))) {
					bForceBlanks = true;
					bForceNonBlanks = true;
				}

				// if the heading is boolean type, it's not available until the dependency heading is selected by user
				if (type == xml.ATTR_BOOLEAN && bForceNonBlanks && bForceBlanks) {
					data.splice(insertIndex++, 0, {
						_count: nBlanks,
						_refs: blankRefs ? _.flatten(blankRefs) : null,
						_text: locale.resource("FILTER_NOT_AVAILABLE"),
						_value: xml.VALUE_BLANK,
						_exclusive: true
					});
				} else {
					// add NONBLANK item
					if (bForceNonBlanks) {
						data.splice(insertIndex++, 0, {
							_count: nNonBlanks,
							_refs: nonblankRefs ? _.flatten(nonblankRefs) : null,
							_text: locale.resource(xml.VALUE_NONBLANK),
							_value: xml.VALUE_NONBLANK,
							_exclusive: true
						});
					}

					// add BLANK item
					if (bForceBlanks) {
						data.splice(insertIndex++, 0, {
							_count: nBlanks,
							_refs: blankRefs ? _.flatten(blankRefs) : null,
							_text: locale.resource(xml.VALUE_BLANK),
							_value: xml.VALUE_BLANK
						});
					}
				}
			}

			if (this._prefilled) {
				// update count for prefilled items with counts from data in server response
				_.each(data, function(itemWithCounts) {
					var item = store.get(itemWithCounts._value);
					if (item) {
						item._refs = itemWithCounts._refs;
						item._count = Number(itemWithCounts._count);
					}
				});

				// update text with count for all prefilled items
				store.query().forEach(function(item) {
					if (item._value != _emptyItem._value) {
						item._textCount = item._text + " (" + item._count + ")" + (item._extraInfo || "");
					}
				});
			} else {
				// add index to data
				// FIX: for right now, we do not track index on special values - we treat them specially in multi-select widget;
				// we should try to move this logic solely to that widget
				this._indexData(data);

				// add selected items if not already in data at beginning - order should not matter as they should be selected and moved to top of multi-select widget
				_.each(_specialItems, function(item, i) {
					data.splice(insertIndex++, 0, item);
				});

				// add counts if desired
				if (this.bCounts) {
					_.each(data, function(item) {
						const extraInfo = item._extraInfo || "";
						item._textCount = (item._count > 0) ?
							`${item._text} (${item._count})${extraInfo}` :
							`${item._text}${extraInfo}`;
					}, this);
				}

				// add empty item to store if so configured
				if (this.bEmptyItem) {
					data = _addEmptyItem(data);
				}

				// finally set data on internal store
				this._setData(data);
			}
		},

		/**
		 * Prefill values when requesting values for boolean attributes
		 * @param {Object} [query]
		 * @param {Object} [options]
		 * @return {boolean}
		 */
		_prefillData: function(query, options) {
			if (this.bPrefill && this.spec && (this.spec.querySpec.type == xml.ATTR_BOOLEAN)) {
				if (this.spec.ra && !this.spec.ra.isOptional && this.spec.inSameDomain) {
					this.bBlanks = false;
					this.bBlankFilters = false;
				}
				this._onData(_getBooleanData());
				return true;
			}
			return false;
		},

		_onResponse: function(/*Response*/res, /*Request*/req, /*Object*/options) {
			// summary: extract data from response and populate store

			// cache any nodes returned, which can happen if getting ref values
			var ctx = this.ctx;
			if (ctx) {
				ctx.cache.cacheResponse(res);
			}

			this._convertToData(res.getValues(), res.getResultNodes());
		},

		_onStoreOptions: function(/*Object*/options) {
			// summary: add blank value
			if (options.requestParameters && this.requestOmitParameters) {
				options.requestParameters = _.omit(options.requestParameters, this.requestOmitParameters);
			}

			if (options.bEmptyItem) {
				options.data = _addEmptyItem(options.data, true);
			}

			if (options.ctx && options.spec && (options.spec.index != null)) {
				options.ctx.keyValue = options.spec.index;
			}
		},

		_sortData: function(/*Object[]*/data) {
			// summary: sort values and remove any potential duplicates in terms of text value (i.e. two dates on same day but different hour value)
			var ctx = this.ctx;
			var spec = this.spec;
			var type = spec && spec.querySpec.type;
			var h = spec && spec.heading;
			var ren = h && csiHeading.getHeadingRenderer(null, h);

			// determine sort value for each item, making sure all blank values go to end
			var sortValues = _.map(data, function(item) {
				var v = item._value;

				if (!item._text) {
					// undefined values will fall to end of sort list, while empty will be first and null somewhere else;
					// so make sure to normalize empty and null values to undefined so they sort together
					v = undefined;
				} else if (ren) {
					v = ren.formatValueForSorting(v, h.m_format, item._text);
				} else if (!_.isNumber(v)) {
					// if no renderer to get proper sort value, and value is not numeric, then sort by text rather than value
					v = item._text;
				}

				return v;
			});

			data = csi.sortByArray(data, sortValues);

			// merge items with same text if numeric value
			if (type && (xml.attributeTypeIsNumeric(type) || (type == xml.ATTR_TIME))) {
				var last;

				data = _.filter(data, function(obj, i) {
					if (i && (last._text == obj._text)) {
						last._count += obj._count;
						if (obj._refs) {
							last._refs = (last._refs || []).concat(obj._refs);
						}
						last._merged = last._merged || [];
						last._merged.push(obj._value);
						return false;
					}

					last = obj;
					return true;
				});
			}

			// apply CSS to filter values if renderer has function to do so
			if (ren && ren.toFilterValueCSS) {
				_.each(data, function(item) {
					item._css = ren.toFilterValueCSS(ctx, h, item._value);
				});
			}

			// apply invalid flag to filter values if renderer has function to do so
			if (ren && ren.toFilterValueExtra) {
				_.each(data, function(item) {
					item._extraInfo = ren.toFilterValueExtra(ctx, h, item._value);
				});
			}

			return data;
		}
	});

	_Values.createFromBooleans = function(/*Item[]*/values, /*Object*/params) {
		// summary: create ValuesStore from boolean values
		return this.createFromValues(csi.sort(_getBooleanData(), "_text"), params);
	};

	_Values.createFromNodes = function(/*Node[]*/nodes, /*Object*/params) {
		// summary: create ValuesStore from list of nodes
		var store = new _Values(params);

		console.assert(!!params.ctx && !!params.spec);

		store.requestModule = null;
		store._convertToData(null, nodes);
		return store;
	};

	_Values.createFromServerValues = function(/*Item[]*/values, /*Object*/params) {
		// summary: create ValuesStore from list of nodes
		var store = new _Values(params);
		store.requestModule = null;
		store._convertToData(values);
		return store;
	};

	_Values.createFromMemoryStore = function(/*Memory*/store, /*Object*/params) {
		// summary: create ValuesStore from given Memory store (can assume synchronous query)
		var id = store.idProperty;
		var label = store.labelProperty || id;

		var values = store.query().map(function(item) {
			return {_text: item[label], _value: item[id]};
		});

		return this.createFromValues(values, params);
	};

	_Values.createFromValues = function(/*Item[]*/values, /*Object*/params) {
		// summary: create ValuesStore from list of nodes
		var store = new _Values(params);
		store.requestModule = null;
		store._indexData(values);
		store._setData(values);
		return store;
	};

	return _Values;
});